import type { Attachment } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/attachment";
import PaperclipIcon from "@redotech/redo-icons/paperclip.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import CircleSpinner from "@redotech/redo-web/circle-spinner.svg";
import { Flex } from "@redotech/redo-web/flex";
import { cn } from "@redotech/redo-web/tailwind-utils";
import { Text } from "@redotech/redo-web/text";
import { Option } from "effect";
import { KEYS, type Value } from "platejs";
import { Plate, PlateContent } from "platejs/react";
import {
  memo,
  type ClipboardEvent,
  type DragEvent,
  type ReactNode,
} from "react";
import type { PendingAttachment } from "../../hooks/use-message-input-attachments";
import type { ReplyMode } from "../../hooks/use-reply-mode";
import * as css from "./message-editor.module.css";
import { handleRichPaste } from "./rich-paste";
import type { UseMessageEditorResult } from "./use-message-editor";

export const MessageEditor = memo(function MessageEditor({
  editorState,
  header,
  toolbar,
  placeholder = "Type your message...",
  onChange,
  className,
  attachments,
  pendingAttachments,
  onRemoveAttachment,
  actions,
  aiBar,
  bottomLeft,
  quotedThread,
  replyMode,
  disabled = false,
  banner,
  signatureHtml,
  textSize = "sm",
  preserveRichPaste = false,
  onFileDrop,
  pendingAutomations,
}: {
  editorState: UseMessageEditorResult;
  header?: ReactNode;
  toolbar?: ReactNode;
  placeholder?: string;
  onSend?: () => void;
  onChange?: (event: { value: Value }) => void;
  className?: string;
  attachments?: Attachment[];
  /** Files currently uploading, rendered as loading chips alongside attachments. */
  pendingAttachments?: PendingAttachment[];
  onRemoveAttachment?: (index: number) => void;
  onDeleteDraft?: () => void;
  isSendingOrDeleting?: boolean;
  actions?: ReactNode;
  /**
   * Optional AI compose bar rendered inside the input box, just above the
   * actions row. Lives within the bordered container so the AI input reads as
   * a sub-input of the composer.
   */
  aiBar?: ReactNode;
  /** Optional content rendered in the bottom-left of the input box (e.g. sources). */
  bottomLeft?: ReactNode;
  /**
   * Optional quoted reply thread rendered below the composed reply, inside the
   * bordered composer (Gmail's "•••" expand-the-thread-you're-replying-to).
   */
  quotedThread?: ReactNode;
  replyMode?: ReplyMode;
  /** When true, typing is prevented and styling dims the composer. */
  disabled?: boolean;
  /** Optional notice rendered above the editor (e.g. a block explanation). */
  banner?: ReactNode;
  /** Email signature HTML to display inline at the bottom of the editor. */
  signatureHtml?: string | null;
  /**
   * Size of the typed text inside the editor. Defaults to `"sm"` (14px) so
   * the support-ticket reply composer keeps its existing look; the macro
   * editor passes `"xs"` (12px) to match the arbiter input sizing used by
   * its sibling fields.
   */
  textSize?: "xs" | "sm";
  /**
   * Preserve rich clipboard content from sources like Excel. Callers opt in
   * only for rich email composer surfaces so social/plain-text channels keep
   * their default paste behavior.
   */
  preserveRichPaste?: boolean;
  /**
   * Called when files are dropped onto the composer. Lets merchants drag files
   * straight into the reply box instead of going through the paperclip. Omit on
   * channels that don't accept file attachments (e.g. social DMs).
   */
  onFileDrop?: (files: File[]) => void;
  /**
   * Optional pending automation chips rendered above the action row so the
   * merchant can see what will be applied when they click send.
   */
  pendingAutomations?: ReactNode;
}) {
  const { editor } = editorState;
  const dropEnabled = onFileDrop !== undefined && !disabled;

  // Point the editor's `FileDropPlugin` at the current per-channel handler (or
  // none, when drops are disabled). The plugin claims OS file drops/pastes
  // through Slate's own `insertData` transform — the seam `@platejs/media` uses
  // for pasted images — so there are no DOM listeners or event props on the
  // editable. This is a ref write, not state: it runs during render but the
  // plugin only reads it inside event handlers, so no effect is needed.
  editorState.setFileDropHandler(dropEnabled ? onFileDrop : undefined);

  // `FileDropPlugin` only sees drops onto the editable. This net covers the rest
  // of the bordered composer — toolbar, attachment tray, padding — so a stray
  // drop there attaches the file instead of letting the browser navigate to it
  // (which would blow away the draft). It defers to the plugin for editable
  // drops via `defaultPrevented`, which Slate sets when it handles a drop.
  const handleContainerDragOver = (event: DragEvent<HTMLElement>) => {
    if (!dropEnabled) {
      return;
    }
    // preventDefault on dragover is required for the drop event to fire. Only
    // claim drags that carry files; let text/selection drags fall through.
    if (Array.from(event.dataTransfer.types).includes("Files")) {
      event.preventDefault();
    }
  };
  const handleContainerDrop = (event: DragEvent<HTMLElement>) => {
    if (!dropEnabled || event.defaultPrevented) {
      return;
    }
    const files = Array.from(event.dataTransfer.files);
    if (files.length === 0) {
      return;
    }
    event.preventDefault();
    onFileDrop?.(files);
  };
  const hasAttachments =
    (attachments !== undefined && attachments.length > 0) ||
    (pendingAttachments !== undefined && pendingAttachments.length > 0);
  const isInternalNote = replyMode === "internalNote";
  // Returning the claim (not just calling `preventDefault`) is what stops Plate
  // from also running Slate's paste, which would insert the content twice.
  const handlePaste = (event: ClipboardEvent<HTMLDivElement>): boolean => {
    if (!preserveRichPaste || disabled || !editor) {
      return false;
    }
    return handleRichPaste<Value>(event, {
      insertParagraphs: (lines, options) =>
        editor.tf.insertNodes(
          lines.map((line) => ({ type: KEYS.p, children: [{ text: line }] })),
          options,
        ),
      parseHtml: (html) => {
        const nodes = editorState.tryHtmlToPlateContent(html);
        return nodes && nodes.length > 0 ? nodes : null;
      },
      insertFragment: (fragment) => editor.tf.insertFragment(fragment),
      focus: () => editor.tf.focus(),
    });
  };
  return (
    <Flex
      className={cn(
        "rounded-md border overflow-hidden",
        !(actions || bottomLeft) && "pb-2",
        "[&_[contenteditable]]:min-h-16",
        isInternalNote
          ? css.internalNoteEditor
          : "border-input bg-background focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]",
        disabled && "opacity-80",
        className,
      )}
      dir="column"
      gap="none"
      onDragOver={handleContainerDragOver}
      onDrop={handleContainerDrop}
    >
      {header}
      {banner && <div className="px-3 pt-3">{banner}</div>}
      <Plate editor={editor} onChange={onChange} readOnly={disabled}>
        {toolbar}
        <PlateContent
          aria-disabled={disabled || undefined}
          className={cn(
            "max-h-[200px] overflow-y-auto outline-none p-3",
            textSize === "xs" ? "text-xs" : "text-sm",
            // Keyed off Slate's block marker, not `p`: the live editor renders
            // paragraphs as `div`, and the reset zeroes their margins.
            "[&>[data-slate-node=element]]:mb-2",
            "[&>[data-slate-node=element]:last-child]:mb-0",
            "placeholder:text-muted-foreground",
            // Reset placeholder styles — Slate inherits bold/italic/underline from the current marks
            "[&_[data-slate-placeholder]]:!font-normal [&_[data-slate-placeholder]]:!not-italic [&_[data-slate-placeholder]]:!no-underline [&_[data-slate-placeholder]]:![text-decoration:none]",
            disabled && "cursor-not-allowed text-muted-foreground",
          )}
          onPaste={handlePaste}
          placeholder={disabled ? "" : placeholder}
        />
        {signatureHtml && (
          <div
            className="border-t border-gray-200 px-3 pb-1 pt-2 text-sm text-gray-600"
            dangerouslySetInnerHTML={{ __html: signatureHtml }}
          />
        )}
      </Plate>
      {quotedThread && (
        <Flex
          className={`border-t border-border ${css.quotedThread}`}
          overflowY="auto"
          px="lg"
          py="md"
        >
          {quotedThread}
        </Flex>
      )}
      {hasAttachments && (
        <Flex
          className="border-t border-border"
          gap="md"
          px="lg"
          py="md"
          wrap="wrap"
        >
          {attachments?.map((attachment, index) => {
            const description = Option.getOrElse(
              attachment.description,
              () => "Attachment",
            );
            const displayName =
              description || attachment.url.split("/").pop() || "Attachment";

            return (
              <Flex
                align="center"
                className="rounded-md bg-muted text-xs"
                gap="sm"
                key={attachment.id}
                px="md"
                py="sm"
              >
                <PaperclipIcon className="size-3 shrink-0 text-muted-foreground" />
                <Text as="span" className="truncate" style={{ maxWidth: 150 }}>
                  {displayName}
                </Text>
                <button
                  className="ml-0.5 rounded-sm p-0.5 hover:bg-background"
                  onClick={() => onRemoveAttachment?.(index)}
                  type="button"
                >
                  <XCloseIcon className="size-3" />
                </button>
              </Flex>
            );
          })}
          {pendingAttachments?.map((pending) => (
            <Flex
              align="center"
              className="rounded-md bg-muted text-xs opacity-70"
              gap="sm"
              key={pending.id}
              px="md"
              py="sm"
            >
              <CircleSpinner className="size-3 shrink-0 text-muted-foreground" />
              <Text as="span" className="truncate" style={{ maxWidth: 150 }}>
                {pending.filename}
              </Text>
            </Flex>
          ))}
        </Flex>
      )}
      {aiBar && (
        <Flex px="lg" py="lg">
          {aiBar}
        </Flex>
      )}
      {pendingAutomations && (
        <Flex className="flex-wrap gap-1.5 px-3 pb-1 pt-1">
          {pendingAutomations}
        </Flex>
      )}
      {(actions || bottomLeft) && (
        <Flex
          align="center"
          className="min-w-0 overflow-hidden"
          gap="md"
          justify={bottomLeft ? "space-between" : "flex-end"}
          pb="lg"
          pt="md"
          px="lg"
        >
          {bottomLeft}
          {actions}
        </Flex>
      )}
    </Flex>
  );
});
